import React from "react";
import {
  Box,
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { BsChevronDown, BsChevronUp, BsPlusLg, BsThreeDotsVertical } from "react-icons/bs";
import { sortBySequence } from "../../utils/riskReviewFormUtils";

const RiskReviewTemplateCanvas = ({
  version,
  selected,
  onSelectSection,
  onSelectField,
  onAddSection,
  onAddField,
  onMoveSection,
  onMoveField,
  isReadOnly,
}) => {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const selectedBorderColor = useColorModeValue("blue.500", "blue.300");
  const mutedBg = useColorModeValue("gray.50", "gray.700");
  const fieldBg = useColorModeValue("white", "gray.800");

  return (
    <Box bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4}>
      <HStack justify="space-between" align="flex-start" mb={4}>
        <Box>
          <Text fontWeight="semibold">Form Canvas</Text>
          <Text color="gray.500" fontSize="sm">
            Arrange sections and choose a field to edit its properties.
          </Text>
        </Box>
      </HStack>
      <VStack align="stretch" spacing={4}>
        {sortBySequence(version?.sections || []).map((section, sectionIndex) => (
          <Box
            key={section.code}
            borderWidth="1px"
            borderColor={selected?.type === "section" && selected.code === section.code ? selectedBorderColor : borderColor}
            borderRadius="md"
            p={4}
            cursor="pointer"
            onClick={() => onSelectSection?.(section.code)}
            bg={selected?.type === "section" && selected.code === section.code ? mutedBg : "transparent"}
          >
            <HStack justify="space-between">
              <Box minW={0}>
                <Text fontWeight="semibold">{section.name}</Text>
                <Text color="gray.500" fontSize="sm" noOfLines={1}>
                  {section.description || section.code}
                </Text>
              </Box>
              <Menu>
                <MenuButton
                  as={IconButton}
                  size="sm"
                  variant="ghost"
                  aria-label="Section actions"
                  icon={<BsThreeDotsVertical />}
                  onClick={(event) => event.stopPropagation()}
                />
                <MenuList onClick={(event) => event.stopPropagation()}>
                  <MenuItem
                    icon={<BsChevronUp />}
                    isDisabled={isReadOnly || sectionIndex === 0}
                    onClick={() => onMoveSection?.(section.code, -1)}
                  >
                    Move up
                  </MenuItem>
                  <MenuItem
                    icon={<BsChevronDown />}
                    isDisabled={isReadOnly || sectionIndex === (version?.sections || []).length - 1}
                    onClick={() => onMoveSection?.(section.code, 1)}
                  >
                    Move down
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
            <VStack align="stretch" mt={3} spacing={2}>
              {sortBySequence(section.fields || []).map((field, fieldIndex) => (
                <HStack
                  key={field.code}
                  justify="space-between"
                  borderWidth="1px"
                  borderColor={selected?.type === "field" && selected.code === field.code ? selectedBorderColor : borderColor}
                  borderRadius="md"
                  px={3}
                  py={2}
                  bg={selected?.type === "field" && selected.code === field.code ? mutedBg : fieldBg}
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectField?.(section.code, field.code);
                  }}
                >
                  <Box minW={0}>
                    <Text fontWeight="medium" noOfLines={1}>{field.label}</Text>
                    <Text color="gray.500" fontSize="xs" noOfLines={1}>{field.code}</Text>
                  </Box>
                  <HStack>
                    <Tag size="sm" variant="subtle">
                      {field.inputType}
                    </Tag>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Move field up"
                      icon={<BsChevronUp />}
                      isDisabled={isReadOnly || fieldIndex === 0}
                      onClick={(event) => {
                        event.stopPropagation();
                        onMoveField?.(section.code, field.code, -1);
                      }}
                    />
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Move field down"
                      icon={<BsChevronDown />}
                      isDisabled={isReadOnly || fieldIndex === (section.fields || []).length - 1}
                      onClick={(event) => {
                        event.stopPropagation();
                        onMoveField?.(section.code, field.code, 1);
                      }}
                    />
                  </HStack>
                </HStack>
              ))}
              <Button size="sm" variant="outline" leftIcon={<BsPlusLg />} isDisabled={isReadOnly} onClick={(event) => {
                event.stopPropagation();
                onAddField?.(section.code);
              }}>
                Add field
              </Button>
            </VStack>
          </Box>
        ))}
        {!version?.sections?.length ? (
          <Text color="gray.500">No sections have been configured for this version.</Text>
        ) : null}
        <Button variant="outline" colorScheme="blue" isDisabled={isReadOnly} onClick={onAddSection}>
          Add section
        </Button>
      </VStack>
    </Box>
  );
};

export default RiskReviewTemplateCanvas;
